import React from "react";
import { LOGO_URL, COMPANY } from "@/lib/site-data";
import { HOME } from "@/constants/testIds";
import { Phone, EnvelopeSimple, MapPin, Clock } from "@phosphor-icons/react";

export default function Footer() {
  return (
    <footer
      id="contact"
      data-testid={HOME.footer}
      className="bg-slate-900 text-slate-300 relative overflow-hidden"
    >
      <div className="bg-grid-dark absolute inset-0 opacity-50 pointer-events-none" />
      <div className="relative">
        {/* CTA bar */}
        <div className="border-b-2 border-slate-800">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-sky-400">
                Need a bin today?
              </div>
              <div className="font-display font-black text-2xl sm:text-3xl text-white mt-2">
                Call us &mdash; we&apos;ll get back to you fast.
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm uppercase tracking-[0.14em] border-2 border-sky-500 hover:border-sky-600 transition-colors"
              >
                <Phone size={18} weight="bold" /> {COMPANY.phone}
              </a>
              <a
                href="/book"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm uppercase tracking-[0.14em] border-2 border-sky-500 hover:border-sky-600 transition-colors"
              >
                Book now
              </a>
            </div>
          </div>
        </div>

        {/* Main */}
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Troya Mini Bins" className="h-12 w-12 object-contain" />
              <div>
                <div className="font-display font-black text-white">TROYA <span className="text-sky-400">MINI BINS</span></div>
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-400">Quality You Can Trust</div>
              </div>
            </div>
            <p className="mt-5 text-sm text-slate-400 leading-relaxed">
              Family-run roll-off bin rentals serving Toronto and the GTA. Fast, transparent, dependable.
            </p>
          </div>

          <div>
            <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-sky-400">Services</div>
            <ul className="mt-5 space-y-3 text-sm">
              {["Mixed Waste", "Shingles", "Concrete", "Soil / Dirt", "Brick", "Metal"].map((s) => (
                <li key={s}>
                  <a href="#pricing" className="hover:text-white transition-colors">
                    {s}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-sky-400">Company</div>
            <ul className="mt-5 space-y-3 text-sm">
              {[
                { l: "Bin Sizes", h: "#sizes" },
                { l: "Pricing", h: "#pricing" },
                { l: "Service Area", h: "#service-area" },
                { l: "FAQ", h: "#faq" },
                { l: "Book Now", h: "/book" },
              ].map((i) => (
                <li key={i.l}>
                  <a href={i.h} className="hover:text-white transition-colors">
                    {i.l}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="https://www.homestars.com/profile/2970106-troya-minibins"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-homestars-link"
                  className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors font-semibold"
                >
                  Reviews on HomeStars
                  <span aria-hidden>↗</span>
                </a>
              </li>
              <li>
                <a
                  href="https://share.google/0fcdpjMFutp7iVbmp"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-google-link"
                  className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 transition-colors font-semibold"
                >
                  Find us on Google
                  <span aria-hidden>↗</span>
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-sky-400">Contact</div>
            <ul className="mt-5 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone size={18} weight="bold" className="text-sky-400 mt-0.5 shrink-0" />
                <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-white">{COMPANY.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <EnvelopeSimple size={18} weight="bold" className="text-sky-400 mt-0.5 shrink-0" />
                <a href={`mailto:${COMPANY.email}`} className="hover:text-white break-all">{COMPANY.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} weight="bold" className="text-sky-400 mt-0.5 shrink-0" />
                <span>{COMPANY.area}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={18} weight="bold" className="text-sky-400 mt-0.5 shrink-0" />
                <span>{COMPANY.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t-2 border-slate-800">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
            <div>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</div>
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 bg-sky-400" />
              <span className="tracking-[0.18em] uppercase">Locally owned · Toronto, ON</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
